import React, { useMemo } from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { TablePagination } from '../../../components';
import { formatIsoToDate, money } from '../../../utils';

import './styles.scoped.css';

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

// A suppressed Total Payment Applied Amount arrives as '' (see suppressRepeatPaymentAmounts);
// ONACCT rows have null invoice-side values. Both render blank rather than "$NaN".
const moneyCell = ({ value }) => (value === '' || value === null || value === undefined ? '' : money.format(value));

// PRST-136 (UAT): sp_cl_cp_bi_pr emits dates as yyyy-MM-dd; the tester asked for MM/DD/YYYY.
// Formatting in the Cell rather than in the proc or the accessor leaves the underlying ISO
// string in place, so react-table's sort on these columns stays chronological instead of
// ordering month-first. formatIsoToDate returns '' for a null/empty date (ONACCT rows carry
// no invoice date), so a blank cell never renders as "Invalid Date".
export const dateCell = ({ value }) => formatIsoToDate(value);

const columnClassName = 'align-middle text-nowrap';
const moneyClassName = 'align-middle text-nowrap text-right';

// Headers are the ticket's quoted labels (UI and CSV); accessors are the sp_cl_cp_bi_pr aliases
// and must match them byte-for-byte or the column renders blank with no error.
export const paymentRegisterColumns = [
  { Header: 'Company', accessor: 'Company', className: columnClassName },
  { Header: 'AMC', accessor: 'AMC', className: columnClassName },
  { Header: 'BillTo #', accessor: 'BillToNumber', className: columnClassName },
  { Header: 'Customer Name', accessor: 'CustomerName', className: columnClassName },
  { Header: 'Payment Number', accessor: 'PaymentNumber', className: columnClassName },
  // PRST-133: relabeled from "Transaction Type" — this is the credit document type
  // (CASH/CM/ONACCT), not the raw transaction type added below.
  { Header: 'Credit Doc Type', accessor: 'TransactionType', className: columnClassName },
  { Header: 'Transaction Type', accessor: 'TransType', className: columnClassName },
  { Header: 'TRNTYPE', accessor: 'TRNTYPE', className: columnClassName },
  // PRST-136: relabeled from "Payment Amount" / "Amount" — UAT asked for the applied-amount
  // wording. Accessors stay on the proc aliases; the Headers also become the CSV header row.
  { Header: 'Total Payment Applied Amount', accessor: 'PaymentAmount', className: moneyClassName, Cell: moneyCell },
  { Header: 'Invoice Applied Amount', accessor: 'Amount', className: moneyClassName, Cell: moneyCell },
  // PRST-136: isDate marks the columns csvColumns must format too (react-table ignores
  // column props it doesn't know, same as the custom className key above).
  { Header: 'Payment Date', accessor: 'PaymentDate', className: columnClassName, Cell: dateCell, isDate: true },
  { Header: 'Posted Date', accessor: 'PostedDate', className: columnClassName, Cell: dateCell, isDate: true },
  { Header: 'Invoice Number', accessor: 'InvoiceNumber', className: columnClassName },
  { Header: 'Invoice Date', accessor: 'InvoiceDate', className: columnClassName, Cell: dateCell, isDate: true },
  { Header: 'Invoice Amount', accessor: 'InvoiceAmount', className: moneyClassName, Cell: moneyCell },
  { Header: 'Invoice Aging Days', accessor: 'InvoiceAgingDays', className: columnClassName },
];

const PaymentRegisterTable = ({ data, loading }) => {
  const memoData = useMemo(() => data, [data]);

  // 50 rows per page per PRST-130 (other Edge reports use 200). No default sort: rows arrive
  // in the proc's order (customer, bill-to, payment number, payment date).
  const tableInstance = useTable(
    {
      columns: paymentRegisterColumns,
      data: memoData,
      initialState: { pageSize: 50 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td {...cell.getCellProps({ className: cell.column.className, style: cell.column.style })}>
        {cell.render('Cell')}
      </td>
    ));
    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div className="spinner-row">{loading && <Spinner animation="border" />}</div>
        </Col>
      </Row>
    </Container>
  );
};

export default PaymentRegisterTable;
